import React, { useEffect, useRef, useState } from "react";
import navImg from "../assets/navimg-removebg-preview.png";
import { brand } from "../data/siteData";
import "../styles/SplashScreen.css";

// Full-screen intro splash shown once per browser session, the first
// time someone lands on the site. Counts a progress ring/bar from
// 0 -> 100 while brand assets settle in, then fades out and reveals
// the site underneath. Skips itself (no flash, no delay) on every
// subsequent in-app navigation because App only mounts it once.
export default function SplashScreen({ onFinish }) {
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const finishedRef = useRef(false);

  useEffect(() => {
    // Respect users who've asked the OS/browser to minimize motion —
    // just complete instantly instead of animating the count.
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setProgress(100);
      finish();
      return;
    }

    const duration = 1900; // ms — total time for 0 -> 100
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const elapsed = now - start;
      // ease-out-ish curve so it feels like it's "loading" rather than
      // ticking linearly — quick at first, settles in near the end.
      const linear = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - linear, 2);
      const pct = Math.round(eased * 100);
      setProgress(pct);

      if (linear < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setProgress(100);
        window.setTimeout(finish, 260);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setLeaving(true);
    window.setTimeout(() => {
      onFinish();
    }, 600); // matches the CSS fade-out duration below
  }

  return (
    <div
      className={`tp-splash ${leaving ? "tp-splash--leaving" : ""}`}
      role="status"
      aria-live="polite"
      aria-label={`Loading ${brand.name}, ${progress}%`}
    >
      <div className="tp-splash__glow" />

      <div className="tp-splash__inner">
        <img
          src={navImg}
          alt={brand.name}
          className="tp-splash__logo"
        />

        <div className="tp-splash__bar-track">
          <div
            className="tp-splash__bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="tp-splash__meta">
          <span className="tp-splash__tagline">{brand.tagline}</span>
          <span className="tp-splash__percent">{progress}%</span>
        </div>
      </div>
    </div>
  );
}
